// Wave 20.49ck — Default CdA seeding for untested positions.
//
// Rider only tests hoods and standing? We still want the aero pane
// to show all four core positions (drops / hoods / tops / standing)
// ranked correctly, with the untested ones grayed out and populated
// with a *relative* estimate derived from the tested ones.
//
// The maths is direct: CD_BY_POSITION already encodes the relative
// drag coefficient of each position, so we can anchor to a tested
// position and scale its bestCda by the Cd ratio to seed the
// untested ones:
//
//   cdaUntested = cdaAnchor × (CD_BY_POSITION[untested] / CD_BY_POSITION[anchor])
//
// If nothing is tested yet we fall back to an absolute default
// (nominal 0.36 m² frontal area × CD_BY_POSITION[position]).
//
// Companion to positionDefaults on mobile (same math, same
// numbers — MUST stay in lockstep).

import type { PositionStat } from '../hooks/usePositionsAnalytics';

// Same numbers as platform/shared/aero/physics.py CD_BY_POSITION
// and web/src/vision/positionClassifier.ts CD_BY_CLASS.
export const CD_RATIOS: Record<string, number> = {
  standing:  1.35,
  tops:      1.10,
  hoods:     0.90,
  drops:     0.72,
  aero_tuck: 0.58,
  tt:        0.46,
};

// The 4 core positions we always show ranked in the aero pane,
// worst → best CdA (matches the rider's mental model: highest
// standing at the top, lowest drops at the bottom).
export const CORE_POSITIONS: string[] = ['standing', 'tops', 'hoods', 'drops'];

// Typical rider frontal area (m²) — used only when the rider
// hasn't tested anything yet. As soon as ANY position is tested
// we scale from that instead.
const NOMINAL_FRONTAL_AREA_M2 = 0.36;

export interface SeededPosition {
  class: string;
  bestCda: number;
  // True if the row comes from real measurements; false if it was
  // seeded from CD_RATIOS. UI should gray these out.
  tested: boolean;
  // Which tested position (if any) the seed was scaled from.
  // Null when no positions are tested yet — the row is from the
  // absolute nominal frontal-area default.
  derivedFrom: string | null;
  // Only present when tested — the underlying stat carries session
  // count / hold time / recent bests for the card UI.
  stat: PositionStat | null;
}

/** Pick the tested position with the most data (sessionCount * holdSec
 *  as the rough proxy) so seeded rows anchor to the rider's most
 *  characterised position. Returns null if nothing is tested. */
function pickAnchor(tested: PositionStat[]): PositionStat | null {
  if (tested.length === 0) return null;
  let best: PositionStat | null = null;
  let bestScore = -1;
  for (const p of tested) {
    const score = (p.sessionCount || 1) * Math.max(1, p.totalHoldSec || 1);
    if (score > bestScore) {
      best = p;
      bestScore = score;
    }
  }
  return best;
}

/** Build the full 4-row ranked list of core positions, filling in
 *  untested rows with defaults derived from the anchor position. */
export function seedCorePositions(tested: PositionStat[]): SeededPosition[] {
  const byClass = new Map<string, PositionStat>();
  for (const p of tested) byClass.set(p.class, p);
  const anchor = pickAnchor(tested);

  const seeded: SeededPosition[] = CORE_POSITIONS.map((cls) => {
    const measured = byClass.get(cls);
    if (measured) {
      return {
        class: cls,
        bestCda: measured.bestCda,
        tested: true,
        derivedFrom: null,
        stat: measured,
      };
    }
    const ratio = CD_RATIOS[cls];
    if (anchor) {
      const anchorRatio = CD_RATIOS[anchor.class];
      // Fallback: if the anchor's class isn't in the ratio table
      // (shouldn't happen — every valid class is), scale from the
      // hoods baseline so we still return something usable.
      const scale = anchorRatio && anchorRatio > 0
        ? anchor.bestCda / anchorRatio
        : anchor.bestCda / (CD_RATIOS.hoods ?? 0.90);
      return {
        class: cls,
        bestCda: scale * ratio,
        tested: false,
        derivedFrom: anchor.class,
        stat: null,
      };
    }
    // Nothing tested — absolute default from nominal frontal area.
    return {
      class: cls,
      bestCda: NOMINAL_FRONTAL_AREA_M2 * ratio,
      tested: false,
      derivedFrom: null,
      stat: null,
    };
  });

  // Rank worst → best CdA so the display order matches the rider's
  // ranking mental model (Standing on top, Drops on bottom).
  seeded.sort((a, b) => b.bestCda - a.bestCda);
  return seeded;
}
